import type { FiatCurrency } from '@0xtorch/core'
import type { ActionFilter, HistoryPageSort } from '@pkg/basic'
import { type SQL, asc, desc, sql } from 'drizzle-orm'
import { parseToActionWithTransactionList } from '../parsers/action'
import { parseToActionSource } from '../parsers/actionSource'
import { actionTable } from '../schema'
import { andSqlConditions } from '../sqlConditions'
import type {
  ActionWithTransactionList,
  DatabaseWithTransaction,
} from '../types'
import {
  createDecimalSortKey,
  createWhereConditionsByActionFilterList,
} from '../utils'

const zeroDecimalSortKey = createDecimalSortKey({ value: '0' })

/**
 * action 一覧取得の orderBy を構築する。
 * @param params history sort を受け取る。
 * @returns drizzle の orderBy 句配列を返す。
 */
const createActionOrderBy = ({
  sort,
}: {
  readonly sort: HistoryPageSort | undefined
}): SQL<unknown>[] => {
  if (sort?.type === 'pl') {
    const totalPlSortKeySql = sql`coalesce(${actionTable.totalPlSortKey}, ${zeroDecimalSortKey})`
    return [
      sort.order === 'desc' ? desc(totalPlSortKeySql) : asc(totalPlSortKeySql),
      desc(actionTable.timestamp),
      asc(actionTable.order),
    ]
  }

  const descByTimestamp = sort === undefined ? true : sort.order === 'desc'
  return [
    descByTimestamp ? desc(actionTable.timestamp) : asc(actionTable.timestamp),
    asc(actionTable.order),
  ]
}

type SelectActionListByFilterListParameters = {
  readonly database: DatabaseWithTransaction
  readonly filterList: ActionFilter[]
  readonly offset: number
  readonly limit: number
  readonly sort?: HistoryPageSort
  readonly fiatCurrencyList: readonly FiatCurrency[]
}

export const selectActionListByFilterList = async ({
  database: { database },
  filterList,
  offset,
  limit,
  sort,
  fiatCurrencyList,
}: SelectActionListByFilterListParameters): Promise<
  readonly ActionWithTransactionList[]
> => {
  // 1. actionTable から id だけを取得
  const whereConditions = createWhereConditionsByActionFilterList(filterList)
  const where = andSqlConditions({ conditions: whereConditions })
  const orderBy = createActionOrderBy({ sort })
  const actionIds = await database.query.actionTable.findMany({
    columns: { id: true },
    where,
    offset,
    limit,
    orderBy,
  })
  if (actionIds.length === 0) return []

  // 2. 取得した id で本体を取得
  const result = await database.query.actionTable.findMany({
    where: (fields, operators) =>
      operators.inArray(
        fields.id,
        actionIds.map((a) => a.id),
      ),
    orderBy,
    with: {
      actionTransferTable: {
        with: {
          cryptoCurrencyTable: true,
          nftTable: true,
        },
      },
      nftTable: true,
      transactionList: {
        with: {
          transferList: {
            with: {
              cryptoCurrency: true,
              nft: true,
            },
          },
          nft: true,
        },
      },
    },
  })

  // id順が変わる可能性があるので、最初のidリスト順にソート
  const idOrder = new Map(actionIds.map((a, i) => [a.id, i]))
  result.sort((a, b) => {
    const aIdx = idOrder.get(a.id)
    const bIdx = idOrder.get(b.id)
    if (aIdx === undefined && bIdx === undefined) return 0
    if (aIdx === undefined) return 1
    if (bIdx === undefined) return -1
    return aIdx - bIdx
  })

  // action source は別クエリで取得して、巨大なネストSQLを避ける
  const sourceIds = [...new Set(result.map((data) => data.sourceId))]
  const actionSources = await database.query.actionSourceTable.findMany({
    where: (fields, operators) => operators.inArray(fields.id, sourceIds),
  })
  const sourceMap = new Map(
    actionSources.map((source) => [source.id, parseToActionSource(source)]),
  )

  return result.map((data) => {
    const source = sourceMap.get(data.sourceId)
    if (source === undefined) {
      throw new Error(`action source not found: ${data.sourceId}`)
    }
    return parseToActionWithTransactionList(data, fiatCurrencyList, source)
  })
}
